<template>
  <div class="screen-container">
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <v-layout v-else column>
      <v-flex class="title-screen" xs12>
        <span>Live Events</span>
      </v-flex>
      <div>
        <div v-if="eventCreatedMsg" class="event-created-wrapper">
          <div class="event-created">
            <img src="/assets/done.svg" alt="Event created" class="event-created-img" />
            <p class="event-created-msg">Event successfully created</p>
          </div>
          <div class="close-wrapper" @click="closeMsg()">
            <p class="close-msg">Close</p>
            <v-icon color="#55A35D" class="close-icon">close</v-icon>
          </div>
        </div>
        <div class="create-search-container">
          <main-button v-if="eventQuota > 0" @click="onCreateEventClick">
            <v-icon style="margin-right: 0.5rem">add</v-icon>
            Create event
          </main-button>
          <text-field
            v-if="allEvents.length"
            label="Search Your Live events"
            append-icon="search"
            style="max-width: 24.0625rem"
            @input="onSearchChange"
          />
        </div>
        <div class="event-container">
          <event-card
            v-for="event in events"
            :key="event.id"
            :event="event"
            :organization-type="organizationType"
            :organization-slug="organizationSlug"
          />
        </div>
      </div>
      <simple-snackbar v-bind.sync="snackbar" />
    </v-layout>
    <demo-modal v-if="demoModal" :user="user" @close="demoModal = false" />
  </div>
</template>

<script>
import moment from 'moment'
import { GET_EVENTS_AND_QUOTA } from '@/graphql/event.graphql'
import eventCard from '../../events/event-card.vue'
import { OrganizationType } from '@/types'
import { uniqBy, min, max, round } from 'lodash'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import sumBy from 'lodash/sumBy'
import DemoModal from '@/components/organizations/DemoModal.vue'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'

export default {
  name: 'organization-live-events-list-container',
  components: { DemoModal, eventCard, simpleSnackbar },
  props: {
    organizationSlug: {
      type: String,
    },
    eventCreated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      organizationType: null,
      OrganizationType,
      snackbar: null,
      events: [],
      allEvents: [],
      form: {},
      eventQuota: 0,
      demoModal: false,
      eventCreatedMsg: this.eventCreated,
      search: null,
      user: undefined,
    }
  },
  methods: {
    closeMsg() {
      this.eventCreatedMsg = false
    },
    onCreateEventClick() {
      if (!this.user.isVerifiedByCoreEvent && this.events.length >= 1) {
        this.demoModal = true
      } else {
        this.handleGoTo()
      }
    },
    handleGoTo() {
      if (this.organizationType === this.OrganizationType.REGULAR) {
        this.goTo(this.routes.organization.organizationAddEvent)
      } else if (this.organizationType === this.OrganizationType.THEATER) {
        this.goTo(this.routes.theaterOrganization.theaterOrganizationAddEvent)
      } else if (this.organizationType === this.OrganizationType.SPORT_ORGANIZATION) {
        this.goTo(this.routes.sportOrganization.sportOrganizationAddEvent)
      }
    },
    getLiveEvents(eventList) {
      return eventList.sort((event1, event2) => new Date(event1.from) - new Date(event2.from))
    },
    getEventTemplatesDates(events) {
      const eventDates = {}
      events.map(({ to, from, eventTemplate: { id } }) => {
        if (!eventDates[id]) {
          eventDates[id] = { to, from }
        } else {
          eventDates[id].to = max([to, eventDates[id].to])
          eventDates[id].from = min([from, eventDates[id].from])
        }
      })

      return eventDates
    },
    eventHasPerformancesInFuture(events, eventTemplateDates, extraModules, allLivePerformances) {
      return events
        .map(event => {
          const currentEventPerformances = allLivePerformances.filter(
            performance => performance.eventTemplate.id === event.id,
          )
          const totalTicketsSold = sumBy(
            currentEventPerformances,
            performance => performance.totalTicketsSold,
          )
          const totalNumberOfTickets = sumBy(
            currentEventPerformances,
            performance => performance.totalNumberOfTickets,
          )
          const totalEarnings = sumBy(
            currentEventPerformances,
            performance => performance.totalEarnings,
          )
          const cover = currentEventPerformances.length ? currentEventPerformances[0].cover : null

          return {
            ...event,
            extraModules,
            to: eventTemplateDates[event.id].to,
            from: eventTemplateDates[event.id].from,
            totalTicketsSold,
            totalNumberOfTickets,
            totalEarnings: round(totalEarnings, 2),
            cover,
          }
        })
        .filter(event => moment(event.to).isAfter(moment()))
    },
    onSearchChange(input) {
      this.events = this.allEvents.filter(event =>
        event.title.toLowerCase().includes(input.toLowerCase()),
      )
    },
  },
  apollo: {
    me: {
      query: GET_LOGGED_USER,
      result({ data, loading }) {
        if (!loading && data) {
          this.user = data.me
        }
      },
    },
    getEvents: {
      query: GET_EVENTS_AND_QUOTA,
      variables() {
        return { organizationSlug: this.organizationSlug, filter: 'live' }
      },
      result({ data: { getEvents, me, getOrganization }, loading }) {
        if (!loading) {
          this.organizationType = getOrganization.type
          if (this.organizationType === this.OrganizationType.THEATER) {
            const extraModules = getEvents.length ? getEvents[0].extraModules : {}
            const eventsWithoutTemplates = getEvents.filter(e => !e.eventTemplate)
            const eventWithTemplate = getEvents.filter(e => e.eventTemplate)
            const eventTemplateDates = this.getEventTemplatesDates(eventWithTemplate)
            const livePerformances = this.getLiveEvents(eventWithTemplate || [])
            const liveEventTemplates = uniqBy(
              livePerformances.map(({ eventTemplate }) => eventTemplate),
              'id',
            )
            this.events = [
              ...this.eventHasPerformancesInFuture(
                liveEventTemplates,
                eventTemplateDates,
                extraModules,
                livePerformances,
              ),
              ...eventsWithoutTemplates,
            ]
          } else {
            this.events = this.getLiveEvents(getEvents || [])
          }
          this.allEvents = this.events
          this.eventQuota = me.eventQuota
        }
      },
    },
  },
}
</script>

<style scoped>
.event-container {
  display: flex;
  flex-direction: column;
  max-width: 42.75rem;
  gap: 1rem;
}

@media screen and (max-width: 500px) {
  .event-container {
    gap: 0;
  }
}

@media screen and (max-width: 560px) {
  .event-created-msg {
    font-size: 1rem !important;
    margin-top: 0.5rem;
  }

  .close-msg {
    display: none;
  }

  .close-icon {
    margin-top: 5px;
  }

  .event-container {
    max-width: 100%;
  }
}

.event-created-wrapper {
  background: #eef6ef;
  border-radius: 0.75rem;
  color: #55a35d;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 39.75rem;
  margin-bottom: 2.5rem;
}

.event-created-msg {
  margin-left: 1rem;
  font-size: 1.5rem;
}

.event-created {
  margin-left: 0.75rem;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  color: #55a35d;
  display: flex;
  padding-top: 0.75rem;
}

.close-msg {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  cursor: pointer;
  color: #55a35d;
  margin-top: 0.95rem;
  margin-right: 0.5rem;
}

.event-created-img {
  height: 1.875rem;
  width: 1.875rem;
  margin-top: 0.25rem;
}

.close-wrapper {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-search-container {
  margin-bottom: 2.5rem;
  gap: 1rem;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  max-width: 42.75rem;
  align-items: center;
}

::v-deep .v-messages {
  display: none;
}

::v-deep .v-input__slot {
  margin: 0;
}
</style>
